import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { useSearchParams } from "react-router-dom";

export default function AdminCounters() {
  const [params] = useSearchParams();
  const passcode = params.get("passcode") || "";
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const fetchItems = React.useCallback(async () => {
    if (!passcode) return;
    setLoading(true);
    try {
      const { getAdminCounters } = await import("../../lib/api");
      const list = await getAdminCounters(passcode);
      setItems(Array.isArray(list) ? list : []);
      setError("");
    } catch (e) {
      setError("Failed to fetch counters. Check passcode.");
    } finally {
      setLoading(false);
    }
  }, [passcode]);

  React.useEffect(() => { fetchItems(); }, [fetchItems]);

  // Poll every 5 seconds
  React.useEffect(() => {
    if (!passcode) return;
    const id = setInterval(fetchItems, 5000);
    return () => clearInterval(id);
  }, [passcode, fetchItems]);

  const adjust = async (key, delta) => {
    try {
      const { setAdminCounter } = await import("../../lib/api");
      const current = items.find((x) => x.key === key)?.value || 0;
      const next = Math.max(0, current + delta);
      await setAdminCounter(key, next, passcode);
      fetchItems();
    } catch (e) {
      setError("Failed to update counter.");
    }
  };

  const setExact = async (key, value) => {
    try {
      const { setAdminCounter } = await import("../../lib/api");
      const v = Math.max(0, parseInt(value || 0, 10));
      await setAdminCounter(key, v, passcode);
      fetchItems();
    } catch (e) {
      setError("Failed to set counter.");
    }
  };

  const clearAll = async () => {
    const ok = window.confirm("This will remove all counters (testing data included). Continue?");
    if (!ok) return;
    try {
      const { resetAdminCounters } = await import("../../lib/api");
      await resetAdminCounters("clear", passcode);
      fetchItems();
    } catch (e) {
      setError("Failed to clear counters.");
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-slate-900 dark:text-slate-100">User & Weekly Entry Counters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!passcode && <div className="text-sm text-slate-500">Missing passcode. Go to /admin to generate links.</div>}
          <div className="flex items-center gap-2">
            <button className="px-3 py-1.5 text-xs rounded border" onClick={clearAll} disabled={!passcode}>Clear All Counters</button>
          </div>
          {loading && <div className="text-sm">Loading…</div>}
          {error && <div className="text-sm text-red-600">{error}</div>}
          {!loading && !error && (
            <div className="divide-y divide-slate-200 dark:divide-slate-800">
              {items.length === 0 && (
                <div className="py-6 text-sm text-slate-500">No counters yet. They will appear as users enter sections.</div>
              )}
              {items.map((it) => (
                <div key={it.key} className="py-3 flex items-center justify-between gap-3">
                  <div className="text-sm font-medium">{labelForKey(it.key)}</div>
                  <div className="flex items-center gap-2">
                    <button className="px-2 py-1 text-xs rounded border" onClick={() => adjust(it.key, -1)}>-1</button>
                    <div className="min-w-[48px] text-right text-sm font-semibold">{it.value}</div>
                    <button className="px-2 py-1 text-xs rounded border" onClick={() => adjust(it.key, +1)}>+1</button>
                    <input
                      type="number"
                      inputMode="numeric"
                      className="ml-3 w-20 px-2 py-1 text-sm rounded border"
                      defaultValue={it.value}
                      onBlur={(e) => setExact(it.key, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') setExact(it.key, e.currentTarget.value); }}
                      aria-label={`Set value for ${it.key}`}
                    />
                    <button className="px-2 py-1 text-xs rounded border" onClick={(e) => {
                      const input = (e.currentTarget.parentElement?.querySelector('input'));
                      if (input) setExact(it.key, input.value);
                    }}>Set</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function labelForKey(key) {
  if (key === 'total_site') return 'Total Users to Site';
  if (key === 'intro') return 'Intro';
  if (key.startsWith('week_')) {
    const n = Number(key.split('_')[1] || '');
    if (!Number.isNaN(n)) return `Week ${n}`;
  }
  return key;
}